/**
 * The per-user data schema as code (specs/schema.sql, data schema 3 = version 1 + migrations 2 and 3).
 *
 * `SCHEMA_V1_STATEMENTS` is the schema-1 part of specs/schema.sql without comments and without the
 * dirty-marking triggers; the triggers are GENERATED from `SYNC_TABLES` (contracts/sync.ts) by
 * `dirtyTriggerStatements()`, so a register added to SYNC_TABLES can never be forgotten. Version 1 creates
 * the triggers of the tables it has; migration 3 creates those of the tables it adds. Tests assert parity
 * of a fresh database (1 → 2 → 3) with specs/schema.sql.
 *
 * Seed rows are inserted BEFORE the triggers exist (or with sync_meta.applying = '1'), so seeds are never
 * dirty (never pushed).
 */

import { MODEL_SYNC_TABLES } from '../contracts/models.js';
import { SYNC_TABLES, type SyncTableSpec } from '../contracts/sync.js';

/** Synced tables created by migration 3 (scoring models); version 1 must not create their triggers. */
const V3_TABLES: ReadonlySet<string> = new Set(MODEL_SYNC_TABLES.map((t) => t.table));
/** The synced tables of data schema 1 and 2. */
export const V1_SYNC_TABLES: readonly SyncTableSpec[] = (SYNC_TABLES as readonly SyncTableSpec[]).filter((t) => !V3_TABLES.has(t.table));
/** The synced tables migration 3 adds. */
export const V3_SYNC_TABLES: readonly SyncTableSpec[] = (SYNC_TABLES as readonly SyncTableSpec[]).filter((t) => V3_TABLES.has(t.table));

/** Tables, indexes, FTS and seed rows, in schema.sql order. */
export const SCHEMA_V1_STATEMENTS: readonly string[] = [
  `CREATE TABLE folders (
  id             TEXT PRIMARY KEY,
  name           TEXT NOT NULL,
  icon           TEXT NOT NULL,
  description    TEXT NOT NULL DEFAULT '',
  builtin        INTEGER NOT NULL DEFAULT 0 CHECK (builtin IN (0, 1)),
  is_keep        INTEGER NOT NULL DEFAULT 0 CHECK (is_keep IN (0, 1)),
  retention_days INTEGER CHECK (retention_days IS NULL OR retention_days BETWEEN 1 AND 3650),
  position       INTEGER NOT NULL,
  created_at     INTEGER NOT NULL,
  hlc            TEXT NOT NULL,
  deleted_at     INTEGER
) STRICT`,
  `CREATE TABLE tags (
  id         TEXT PRIMARY KEY,
  name       TEXT NOT NULL,
  name_fold  TEXT NOT NULL,
  icon       TEXT NOT NULL DEFAULT 'tag',
  position   INTEGER NOT NULL,
  created_at INTEGER NOT NULL,
  hlc        TEXT NOT NULL,
  deleted_at INTEGER
) STRICT`,
  `CREATE INDEX tags_name ON tags (name_fold) WHERE deleted_at IS NULL`,
  `CREATE TABLE topics (
  id              TEXT PRIMARY KEY,
  name            TEXT NOT NULL,
  hint            TEXT NOT NULL DEFAULT '',
  icon            TEXT NOT NULL,
  color           TEXT NOT NULL,
  enabled         INTEGER NOT NULL DEFAULT 1 CHECK (enabled IN (0, 1)),
  disabled_reason TEXT CHECK (disabled_reason IN ('user', 'plan')),
  position        INTEGER NOT NULL,
  archived_at     INTEGER,
  created_at      INTEGER NOT NULL,
  hlc             TEXT NOT NULL,
  deleted_at      INTEGER
) STRICT`,
  `CREATE TABLE sources (
  id              TEXT PRIMARY KEY,
  kind            TEXT NOT NULL CHECK (kind IN ('rss','email','youtube','github','telegram',
                                                'mastodon','bluesky','reddit','discourse','watcher')),
  name            TEXT NOT NULL,
  locator         TEXT NOT NULL,
  config          TEXT NOT NULL DEFAULT '{}',
  pull_minutes    INTEGER NOT NULL DEFAULT 15 CHECK (pull_minutes BETWEEN 0 AND 10080),
  format          TEXT NOT NULL DEFAULT 'original' CHECK (format IN ('original', 'reader', 'plain')),
  keep_images     INTEGER NOT NULL DEFAULT 1 CHECK (keep_images IN (0, 1)),
  keep_videos     INTEGER NOT NULL DEFAULT 1 CHECK (keep_videos IN (0, 1)),
  description     TEXT NOT NULL DEFAULT '',
  enabled         INTEGER NOT NULL DEFAULT 1 CHECK (enabled IN (0, 1)),
  disabled_reason TEXT CHECK (disabled_reason IN ('user', 'plan')),
  archived_at     INTEGER,
  created_at      INTEGER NOT NULL,
  hlc             TEXT NOT NULL,
  deleted_at      INTEGER
) STRICT`,
  `CREATE INDEX sources_kind ON sources (kind) WHERE deleted_at IS NULL`,
  `CREATE TABLE source_state (
  source_id      TEXT PRIMARY KEY,
  cursor         TEXT,
  etag           TEXT,
  last_modified  TEXT,
  last_fetch_at  INTEGER,
  last_ok_at     INTEGER,
  last_error     TEXT,
  error_count    INTEGER NOT NULL DEFAULT 0,
  next_due_at    INTEGER NOT NULL DEFAULT 0,
  needs_secret   INTEGER NOT NULL DEFAULT 0 CHECK (needs_secret IN (0, 1))
) STRICT`,
  `CREATE INDEX source_state_due ON source_state (next_due_at)`,
  `CREATE TABLE source_days (
  source_id TEXT NOT NULL,
  day       INTEGER NOT NULL,
  received  INTEGER NOT NULL,
  kept      INTEGER NOT NULL,
  hlc       TEXT NOT NULL,
  PRIMARY KEY (source_id, day)
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE messages (
  seq               INTEGER PRIMARY KEY,
  id                TEXT NOT NULL UNIQUE,
  source_id         TEXT NOT NULL,
  external_id       TEXT NOT NULL,
  received_at       INTEGER NOT NULL,
  published_at      INTEGER,
  sort_at           INTEGER NOT NULL,
  url               TEXT,
  title             TEXT,
  title_fold        TEXT,
  author            TEXT,
  author_fold       TEXT,
  body_html         TEXT,
  excerpt           TEXT NOT NULL DEFAULT '',
  image_url         TEXT,
  media             TEXT,
  word_count        INTEGER NOT NULL DEFAULT 0,
  reading_minutes   INTEGER NOT NULL DEFAULT 1,
  lang              TEXT,
  thread_key        TEXT,
  extra             TEXT,
  content_state     TEXT NOT NULL DEFAULT 'ready' CHECK (content_state IN ('ready', 'pending', 'failed')),
  content_hlc       TEXT NOT NULL,
  folder_id         TEXT NOT NULL,
  folder_entered_at INTEGER NOT NULL,
  placed_by_kind    TEXT NOT NULL CHECK (placed_by_kind IN ('source','filter','scoring','user','mcp','system')),
  placed_by_id      TEXT,
  auto_folder_id    TEXT,
  auto_by_kind      TEXT CHECK (auto_by_kind IN ('filter', 'scoring')),
  auto_by_id        TEXT,
  placement_hlc     TEXT NOT NULL,
  read_at           INTEGER,
  read_hlc          TEXT NOT NULL,
  pipeline_state    TEXT NOT NULL DEFAULT 'new' CHECK (pipeline_state IN ('new', 'scoring', 'done')),
  filter_results    TEXT NOT NULL DEFAULT '{}',
  score_state       TEXT NOT NULL DEFAULT 'none' CHECK (score_state IN ('none','pending','done','skipped','failed')),
  scores            TEXT,
  best_score        INTEGER,
  best_topic_id     TEXT,
  scored_at         INTEGER,
  scoring_model     TEXT,
  auto_hlc          TEXT NOT NULL,
  deleted_at        INTEGER,
  delete_reason     TEXT CHECK (delete_reason IN ('retention', 'rule', 'user', 'mcp', 'source_removed')),
  life_hlc          TEXT NOT NULL
) STRICT`,
  `CREATE INDEX messages_list        ON messages (folder_id, sort_at DESC, id) WHERE deleted_at IS NULL`,
  `CREATE INDEX messages_relevance   ON messages (folder_id, best_score DESC, sort_at DESC) WHERE deleted_at IS NULL`,
  `CREATE INDEX messages_unread      ON messages (folder_id, reading_minutes) WHERE deleted_at IS NULL AND read_at IS NULL`,
  `CREATE INDEX messages_all_time    ON messages (sort_at DESC, id) WHERE deleted_at IS NULL`,
  `CREATE INDEX messages_source      ON messages (source_id, received_at)`,
  `CREATE INDEX messages_received    ON messages (received_at)`,
  `CREATE INDEX messages_dwell       ON messages (folder_id, folder_entered_at) WHERE deleted_at IS NULL`,
  `CREATE INDEX messages_pipeline    ON messages (pipeline_state, received_at) WHERE pipeline_state <> 'done' AND deleted_at IS NULL`,
  `CREATE INDEX messages_thread      ON messages (source_id, thread_key) WHERE thread_key IS NOT NULL`,
  `CREATE INDEX messages_tombstones  ON messages (deleted_at) WHERE deleted_at IS NOT NULL`,
  `CREATE VIRTUAL TABLE messages_fts USING fts5(
  title, body, author,
  content = '',
  contentless_delete = 1,
  tokenize = 'unicode61 remove_diacritics 0'
)`,
  `CREATE TABLE message_topics (
  message_id TEXT NOT NULL,
  topic_id   TEXT NOT NULL,
  score      INTEGER CHECK (score IS NULL OR score BETWEEN 0 AND 100),
  origin     TEXT NOT NULL CHECK (origin IN ('model', 'user', 'mcp')),
  hidden     INTEGER NOT NULL DEFAULT 0 CHECK (hidden IN (0, 1)),
  hlc        TEXT NOT NULL,
  deleted_at INTEGER,
  PRIMARY KEY (message_id, topic_id)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX message_topics_topic ON message_topics (topic_id, score) WHERE deleted_at IS NULL AND hidden = 0`,
  `CREATE TABLE message_tags (
  message_id TEXT NOT NULL,
  tag_id     TEXT NOT NULL,
  by_kind    TEXT NOT NULL CHECK (by_kind IN ('filter', 'scoring', 'user', 'mcp')),
  by_id      TEXT,
  hlc        TEXT NOT NULL,
  deleted_at INTEGER,
  PRIMARY KEY (message_id, tag_id)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX message_tags_tag ON message_tags (tag_id) WHERE deleted_at IS NULL`,
  `CREATE TABLE events (
  id             TEXT PRIMARY KEY,
  message_id     TEXT NOT NULL,
  at             INTEGER NOT NULL,
  kind           TEXT NOT NULL CHECK (kind IN ('moved','read','unread','topics','tags','deleted',
                                               'restored','assessment','rescored')),
  actor_kind     TEXT NOT NULL CHECK (actor_kind IN ('source','filter','scoring','user','mcp','system')),
  actor_id       TEXT,
  from_folder_id TEXT,
  to_folder_id   TEXT,
  detail         TEXT NOT NULL DEFAULT '{}',
  hlc            TEXT NOT NULL,
  deleted_at     INTEGER
) STRICT`,
  `CREATE INDEX events_message ON events (message_id, at)`,
  `CREATE TABLE corrections (
  id              TEXT PRIMARY KEY,
  message_id      TEXT NOT NULL,
  kind            TEXT NOT NULL CHECK (kind IN ('placement', 'topic')),
  from_folder_id  TEXT,
  to_folder_id    TEXT,
  topic_id        TEXT,
  topic_change    TEXT CHECK (topic_change IN ('added', 'removed')),
  auto_actor_kind TEXT NOT NULL CHECK (auto_actor_kind IN ('filter', 'scoring')),
  auto_actor_id   TEXT,
  by_kind         TEXT NOT NULL CHECK (by_kind IN ('user', 'mcp')),
  by_id           TEXT,
  snapshot        TEXT NOT NULL DEFAULT '{}',
  status          TEXT NOT NULL DEFAULT 'open' CHECK (status IN ('open', 'resolved', 'ignored', 'withdrawn')),
  note            TEXT NOT NULL DEFAULT '',
  created_at      INTEGER NOT NULL,
  resolved_at     INTEGER,
  hlc             TEXT NOT NULL,
  deleted_at      INTEGER
) STRICT`,
  `CREATE INDEX corrections_open ON corrections (status, created_at) WHERE deleted_at IS NULL`,
  `CREATE INDEX corrections_message ON corrections (message_id)`,
  `CREATE TABLE rules (
  id                TEXT PRIMARY KEY,
  kind              TEXT NOT NULL CHECK (kind IN ('filter', 'scoring')),
  name              TEXT NOT NULL,
  icon              TEXT NOT NULL,
  enabled           INTEGER NOT NULL DEFAULT 1 CHECK (enabled IN (0, 1)),
  disabled_reason   TEXT CHECK (disabled_reason IN ('user', 'plan')),
  position          INTEGER NOT NULL,
  trigger_folder_id TEXT,
  condition         TEXT NOT NULL,
  actions           TEXT NOT NULL,
  archived_at       INTEGER,
  created_at        INTEGER NOT NULL,
  hlc               TEXT NOT NULL,
  deleted_at        INTEGER,
  CHECK ((kind = 'filter') = (trigger_folder_id IS NOT NULL))
) STRICT`,
  `CREATE INDEX rules_trigger ON rules (kind, trigger_folder_id, position) WHERE deleted_at IS NULL`,
  `CREATE TABLE views (
  id         TEXT PRIMARY KEY,
  name       TEXT NOT NULL,
  icon       TEXT NOT NULL,
  query      TEXT NOT NULL,
  position   INTEGER NOT NULL,
  created_at INTEGER NOT NULL,
  hlc        TEXT NOT NULL,
  deleted_at INTEGER
) STRICT`,
  `CREATE TABLE assessments (
  message_id TEXT PRIMARY KEY,
  body       TEXT NOT NULL,
  by_kind    TEXT NOT NULL CHECK (by_kind IN ('mcp', 'user')),
  by_id      TEXT,
  created_at INTEGER NOT NULL,
  hlc        TEXT NOT NULL,
  deleted_at INTEGER
) STRICT`,
  `CREATE TABLE digests (
  id          TEXT PRIMARY KEY,
  title       TEXT NOT NULL,
  body_md     TEXT NOT NULL,
  period_from INTEGER,
  period_to   INTEGER,
  message_ids TEXT NOT NULL DEFAULT '[]',
  by_kind     TEXT NOT NULL CHECK (by_kind IN ('mcp', 'user')),
  by_id       TEXT,
  created_at  INTEGER NOT NULL,
  hlc         TEXT NOT NULL,
  deleted_at  INTEGER
) STRICT`,
  `CREATE INDEX digests_time ON digests (created_at DESC) WHERE deleted_at IS NULL`,
  `CREATE TABLE connectors (
  id           TEXT PRIMARY KEY,
  name         TEXT NOT NULL,
  icon         TEXT NOT NULL DEFAULT 'plug',
  access       TEXT NOT NULL CHECK (access IN ('read', 'organise', 'full')),
  enabled      INTEGER NOT NULL DEFAULT 1 CHECK (enabled IN (0, 1)),
  token_hint   TEXT NOT NULL DEFAULT '',
  last_used_at INTEGER,
  created_at   INTEGER NOT NULL,
  hlc          TEXT NOT NULL,
  deleted_at   INTEGER
) STRICT`,
  `CREATE TABLE settings (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL,
  hlc   TEXT NOT NULL
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE local_settings (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE engine_config (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE pipeline_jobs (
  message_id  TEXT NOT NULL,
  stage       TEXT NOT NULL CHECK (stage IN ('content', 'scoring')),
  attempts    INTEGER NOT NULL DEFAULT 0,
  next_try_at INTEGER NOT NULL DEFAULT 0,
  last_error  TEXT,
  PRIMARY KEY (message_id, stage)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX pipeline_jobs_due ON pipeline_jobs (stage, next_try_at)`,
  `CREATE TABLE fetch_hints (
  origin     TEXT PRIMARY KEY,
  mode       TEXT NOT NULL CHECK (mode IN ('direct', 'relay')),
  checked_at INTEGER NOT NULL
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE sync_meta (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL
) STRICT, WITHOUT ROWID`,
  `INSERT INTO sync_meta (key, value) VALUES ('applying', '0'), ('mark', '0'), ('seq', '0')`,
  `CREATE TABLE sync_dirty (
  tbl  TEXT NOT NULL,
  id   TEXT NOT NULL,
  grp  TEXT NOT NULL,
  mark INTEGER NOT NULL,
  PRIMARY KEY (tbl, id, grp)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX sync_dirty_mark ON sync_dirty (mark)`,
  `CREATE TABLE sync_seq (
  tbl TEXT NOT NULL,
  id  TEXT NOT NULL,
  grp TEXT NOT NULL,
  seq INTEGER NOT NULL,
  PRIMARY KEY (tbl, id, grp)
) STRICT, WITHOUT ROWID`,
  `CREATE UNIQUE INDEX sync_seq_seq ON sync_seq (seq)`,
  `INSERT INTO folders (id, name, icon, description, builtin, is_keep, retention_days, position, created_at, hlc) VALUES
  ('inbox',     'Inbox',     'inbox',   'New and unprocessed messages',          1, 0, 30, 0, 0, '0000000000000-0000-seed'),
  ('priority',  'Priority',  'star',    'Messages worth reading first',          1, 1, 90, 1, 0, '0000000000000-0000-seed'),
  ('weak',      'Weak',      'signal',  'Lower relevance, available for review', 1, 0, 14, 2, 0, '0000000000000-0000-seed'),
  ('dismissed', 'Dismissed', 'archive', 'Messages set aside',                    1, 0,  7, 3, 0, '0000000000000-0000-seed')`,
  `INSERT INTO rules (id, kind, name, icon, enabled, position, trigger_folder_id, condition, actions, created_at, hlc) VALUES
  ('sr_priority',  'scoring', 'Strong match',  'star',    1, 0, NULL,
   '{"type":"group","operator":"all","children":[{"type":"condition","field":"best-score","operator":"gte","value":50}]}',
   '[{"type":"move","folderId":"priority"}]', 0, '0000000000000-0000-seed'),
  ('sr_weak',      'scoring', 'Loose match',   'signal',  1, 1, NULL,
   '{"type":"group","operator":"all","children":[{"type":"condition","field":"best-score","operator":"gte","value":15}]}',
   '[{"type":"move","folderId":"weak"}]', 0, '0000000000000-0000-seed'),
  ('sr_dismissed', 'scoring', 'Below the bar', 'archive', 1, 2, NULL,
   '{"type":"group","operator":"all","children":[]}',
   '[{"type":"move","folderId":"dismissed"}]', 0, '0000000000000-0000-seed')`,
];

const APPLYING_GUARD = "(SELECT value FROM sync_meta WHERE key = 'applying') = '0'";
const CURRENT_MARK = "(SELECT CAST(value AS INTEGER) FROM sync_meta WHERE key = 'mark')";

/** The wire id expression of a row: key columns joined with '|'. */
export function keyExpression(spec: Pick<SyncTableSpec, 'key'>, alias = 'NEW'): string {
  return spec.key.map((c) => `${alias}.${c}`).join(" || '|' || ");
}

/**
 * Dirty-marking triggers for every register of every synced table (sync.md §3):
 * one AFTER INSERT per table (marking every register) and one AFTER UPDATE OF <hlc> per register.
 * Single-register tables use the names `<table>_dirty_ins` / `<table>_dirty_upd`; multi-register
 * tables `<table>_dirty_ins` / `<table>_dirty_<register>`.
 */
export function dirtyTriggerStatements(tables: readonly SyncTableSpec[] = SYNC_TABLES): string[] {
  const out: string[] = [];
  for (const spec of tables) {
    const id = keyExpression(spec);
    const t = spec.table;
    const single = spec.registers.length === 1;
    if (single) {
      const reg = spec.registers[0] as SyncTableSpec['registers'][number];
      out.push(
        `CREATE TRIGGER ${t}_dirty_ins AFTER INSERT ON ${t}\n` +
          `WHEN ${APPLYING_GUARD}\n` +
          `BEGIN\n  INSERT OR REPLACE INTO sync_dirty (tbl, id, grp, mark)\n` +
          `  VALUES ('${t}', ${id}, '${reg.name}', ${CURRENT_MARK});\nEND`,
      );
    } else {
      const groups = spec.registers.map((r, i) => (i === 0 ? `SELECT '${r.name}' AS grp` : `SELECT '${r.name}'`)).join(' UNION ALL ');
      out.push(
        `CREATE TRIGGER ${t}_dirty_ins AFTER INSERT ON ${t}\n` +
          `WHEN ${APPLYING_GUARD}\n` +
          `BEGIN\n  INSERT OR REPLACE INTO sync_dirty (tbl, id, grp, mark)\n` +
          `  SELECT '${t}', ${id}, g.grp, ${CURRENT_MARK}\n  FROM (${groups}) AS g;\nEND`,
      );
    }
    for (const reg of spec.registers) {
      const name = single ? `${t}_dirty_upd` : `${t}_dirty_${reg.name}`;
      out.push(
        `CREATE TRIGGER ${name} AFTER UPDATE OF ${reg.hlc} ON ${t}\n` +
          `WHEN ${APPLYING_GUARD} AND NEW.${reg.hlc} <> OLD.${reg.hlc}\n` +
          `BEGIN\n  INSERT OR REPLACE INTO sync_dirty (tbl, id, grp, mark)\n` +
          `  VALUES ('${t}', ${id}, '${reg.name}', ${CURRENT_MARK});\nEND`,
      );
    }
  }
  return out;
}

/**
 * Schema version 2 (engine.md §4, §14 and "Amendments 2026-10-05"). ALTER TABLE appends each column after
 * the table's last column, which is where specs/schema.sql spells them. No Hlc is bumped here: every
 * existing (source, day) bucket is queued in `stats_dirty`, and the engine's usefulness recompute rebuilds
 * it (received, kept, folders, deleted) with its own clock and freeze rule, then syncs it as usual.
 * Existing sources get `backfill_items = NULL` = ingest.backfill_items, i.e. exactly what they had.
 * A device that syncs re-pulls from cursor 0 once (sync.md "Schema upgrades").
 */
export const SCHEMA_V2_STATEMENTS: readonly string[] = [
  `ALTER TABLE sources ADD COLUMN backfill_items INTEGER CHECK (backfill_items IS NULL OR backfill_items BETWEEN 1 AND 1000)`,
  `ALTER TABLE source_days ADD COLUMN folders TEXT NOT NULL DEFAULT '{}'`,
  `ALTER TABLE source_days ADD COLUMN deleted INTEGER NOT NULL DEFAULT 0`,
  `CREATE TABLE stats_dirty (
  source_id TEXT NOT NULL,
  day       INTEGER NOT NULL,
  PRIMARY KEY (source_id, day)
) STRICT, WITHOUT ROWID`,
  `INSERT OR IGNORE INTO stats_dirty (source_id, day) SELECT DISTINCT source_id, received_at / 86400000 FROM messages`,
  // A device that already syncs pulls everything once more: a tab still running schema-1 code may have
  // pulled (and, unable to read the new columns, skipped) sources / source_days changes before it reloaded.
  `INSERT OR REPLACE INTO sync_meta (key, value) SELECT 'needs_full_pull', '1' WHERE EXISTS (SELECT 1 FROM sync_meta WHERE key = 'cursor' AND value <> '0')`,
];

export function schemaV2(): string[] {
  return [...SCHEMA_V2_STATEMENTS];
}

/**
 * Data schema 3 — scoring models (models.md §4; the migration is §9.2 and schema/migration-3.ts). DDL only;
 * the data steps run after it. ALTER TABLE appends `phrases` after the last column of `topics`, which is where
 * specs/schema.sql spells it. `pipeline_jobs` gets a new `stage` check; its rows are kept, the stage
 * 'scoring' becoming 'score:jev' (a budget pause survives the upgrade).
 */
export const SCHEMA_V3_STATEMENTS: readonly string[] = [
  `ALTER TABLE topics ADD COLUMN phrases TEXT NOT NULL DEFAULT '[]'`,
  `CREATE TABLE models (
  id              TEXT PRIMARY KEY,
  name            TEXT NOT NULL,
  icon            TEXT NOT NULL,
  enabled         INTEGER NOT NULL DEFAULT 0 CHECK (enabled IN (0, 1)),
  disabled_reason TEXT CHECK (disabled_reason IN ('user', 'plan')),
  position        INTEGER NOT NULL,
  run             TEXT NOT NULL,
  options         TEXT NOT NULL,
  created_at      INTEGER NOT NULL,
  hlc             TEXT NOT NULL,
  deleted_at      INTEGER
) STRICT`,
  `CREATE TABLE message_scores (
  message_id    TEXT NOT NULL,
  model_id      TEXT NOT NULL,
  state         TEXT NOT NULL CHECK (state IN ('pending', 'done', 'failed')),
  scores        TEXT,
  best_score    INTEGER,
  best_topic_id TEXT,
  scored_at     INTEGER,
  model_version TEXT,
  requested_at  INTEGER NOT NULL,
  hlc           TEXT NOT NULL,
  deleted_at    INTEGER,
  PRIMARY KEY (message_id, model_id)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX message_scores_pending ON message_scores (model_id, requested_at) WHERE state = 'pending' AND deleted_at IS NULL`,
  `CREATE INDEX message_scores_done ON message_scores (message_id) WHERE state = 'done' AND deleted_at IS NULL`,
  `CREATE TABLE message_vectors (
  message_id TEXT NOT NULL,
  model_id   TEXT NOT NULL,
  rev        TEXT NOT NULL,
  text_hash  TEXT NOT NULL,
  vec        BLOB NOT NULL,
  created_at INTEGER NOT NULL,
  PRIMARY KEY (message_id, model_id)
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE model_vectors (
  model_id   TEXT NOT NULL,
  kind       TEXT NOT NULL CHECK (kind IN ('topic', 'anchor')),
  ref        TEXT NOT NULL,
  rev        TEXT NOT NULL,
  texts_hash TEXT NOT NULL,
  texts      INTEGER NOT NULL,
  vec        BLOB NOT NULL,
  PRIMARY KEY (model_id, kind, ref)
) STRICT, WITHOUT ROWID`,
  `CREATE TABLE pipeline_jobs_v3_copy AS
  SELECT message_id, CASE stage WHEN 'scoring' THEN 'score:jev' ELSE stage END AS stage, attempts, next_try_at, last_error
  FROM pipeline_jobs`,
  `DROP TABLE pipeline_jobs`,
  `CREATE TABLE pipeline_jobs (
  message_id  TEXT NOT NULL,
  stage       TEXT NOT NULL CHECK (stage = 'content' OR stage LIKE 'score:%'),
  attempts    INTEGER NOT NULL DEFAULT 0,
  next_try_at INTEGER NOT NULL DEFAULT 0,
  last_error  TEXT,
  PRIMARY KEY (message_id, stage)
) STRICT, WITHOUT ROWID`,
  `CREATE INDEX pipeline_jobs_due ON pipeline_jobs (stage, next_try_at)`,
  `INSERT INTO pipeline_jobs (message_id, stage, attempts, next_try_at, last_error)
  SELECT message_id, stage, attempts, next_try_at, last_error FROM pipeline_jobs_v3_copy`,
  `DROP TABLE pipeline_jobs_v3_copy`,
];

/** Migration 3's DDL and the dirty triggers of its two synced tables, in execution order. */
export function schemaV3(): string[] {
  return [...SCHEMA_V3_STATEMENTS, ...dirtyTriggerStatements(V3_SYNC_TABLES)];
}

/** The complete version-1 DDL, in execution order (triggers only for the tables version 1 has). */
export function schemaV1(): string[] {
  return [...SCHEMA_V1_STATEMENTS, ...dirtyTriggerStatements(V1_SYNC_TABLES)];
}
